// Guest-identity helper: a handle that sticks across tabs and browser
// restarts on the same device, so a guest can close the tab, come back
// next week, and still own the same submissions and ranked history.
//
// Resolution order (first hit wins):
//   1. ?user=<handle> query param (force an identity, for testing)
//   2. pb_handle cookie (1-year, same-site Lax, readable by JS + API)
//   3. localStorage.pb_handle (legacy, kept for migration only)
//   4. sessionStorage.pb_handle (older legacy, kept for migration only)
//   5. a fresh random handle written to the cookie
//
// Why a cookie and not localStorage? The cookie travels on every request
// to the API automatically, so server handlers can resolve a guest's
// handle without the client having to send it in the body. Cross-device
// sync still requires sign-in - cookies are device-scoped.
//
// Once auth lands the session takes over; this helper becomes a
// fallback for un-signed-in browsing.

const STORAGE_KEY = 'pb_handle';
const CHANGED_AT_KEY = 'pb_handle_changed_at';
const COOKIE_MAX_AGE_SECONDS = 365 * 24 * 60 * 60; // 1 year

export const HANDLE_CHANGE_COOLDOWN_MS = 24 * 60 * 60 * 1000;

function storage(): Storage | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function legacySessionStorage(): Storage | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const prefix = `${name}=`;
  for (const part of document.cookie.split(';')) {
    const trimmed = part.trim();
    if (trimmed.startsWith(prefix)) {
      try {
        return decodeURIComponent(trimmed.slice(prefix.length));
      } catch {
        return trimmed.slice(prefix.length);
      }
    }
  }
  return null;
}

function writeCookie(name: string, value: string): void {
  if (typeof document === 'undefined') return;
  // Lax is enough: we don't need cross-site, and the handle has no
  // sensitive value. Secure is enabled when the page is served over
  // HTTPS so the prod cookie is HTTPS-only by default.
  const isHttps = typeof location !== 'undefined' && location.protocol === 'https:';
  const parts = [
    `${name}=${encodeURIComponent(value)}`,
    'Path=/',
    `Max-Age=${COOKIE_MAX_AGE_SECONDS}`,
    'SameSite=Lax',
  ];
  if (isHttps) parts.push('Secure');
  document.cookie = parts.join('; ');
}

const ADJECTIVES = [
  'phonky',
  'silky',
  'shadow',
  'neon',
  'velvet',
  'dusty',
  'crisp',
  'sour',
  'wavy',
  'gritty',
  'syrupy',
  'ghost',
  'pulse',
  'tape',
  'raw',
  'glass',
  'glitchy',
  'lunar',
  'echo',
  'analog',
  'sticky',
  'chilled',
  'wired',
] as const;

const NOUNS = [
  'stoat',
  'rabbit',
  'kick',
  'snare',
  'loop',
  '808',
  'raven',
  'lizard',
  'fox',
  'possum',
  'moth',
  'ferret',
  'crane',
  'finch',
  'otter',
  'ox',
  'owl',
  'hare',
  'mantis',
  'eel',
  'gecko',
  'heron',
  'buzz',
] as const;

export function randomHandle(): string {
  const a = ADJECTIVES[Math.floor(Math.random() * ADJECTIVES.length)];
  const n = NOUNS[Math.floor(Math.random() * NOUNS.length)];
  const suffix = Math.floor(Math.random() * 900 + 100); // 100-999
  return `${a}-${n}-${suffix}`;
}

export function readStoredHandle(): string | null {
  // Cookie is primary. Fall back to localStorage / sessionStorage so a
  // user with an in-flight match doesn't lose their identity on the
  // upgrade from the old tab-scoped scheme.
  const fromCookie = readCookie(STORAGE_KEY);
  if (fromCookie) return fromCookie;
  const fromLocal = storage()?.getItem(STORAGE_KEY) ?? null;
  if (fromLocal) return fromLocal;
  const fromSession = legacySessionStorage()?.getItem(STORAGE_KEY) ?? null;
  return fromSession;
}

export function writeStoredHandle(handle: string): void {
  writeCookie(STORAGE_KEY, handle);
  // Mirror to localStorage so SSR snapshots and edge cases that read
  // synchronously before document.cookie is parsed still see the value.
  try {
    storage()?.setItem(STORAGE_KEY, handle);
  } catch {
    /* storage full / private mode - ignore */
  }
}

export function markHandleChanged(now = Date.now()): void {
  try {
    storage()?.setItem(CHANGED_AT_KEY, String(now));
  } catch {
    /* ignore */
  }
}

/** ms remaining until the next allowed change. 0 means the user may change now. */
export function msUntilNextHandleChange(now = Date.now()): number {
  const raw = storage()?.getItem(CHANGED_AT_KEY);
  if (!raw) return 0;
  const last = Number.parseInt(raw, 10);
  if (!Number.isFinite(last)) return 0;
  const remaining = last + HANDLE_CHANGE_COOLDOWN_MS - now;
  return remaining > 0 ? remaining : 0;
}

export function formatCooldown(ms: number): string {
  if (ms <= 0) return 'now';
  const hours = Math.floor(ms / (60 * 60 * 1000));
  const minutes = Math.floor((ms % (60 * 60 * 1000)) / (60 * 1000));
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m`;
  return 'under 1m';
}

/** Resolve the handle for this browser: URL override → storage → random. */
export function resolveHandle(urlOverride?: string | null): string {
  const trimmedOverride = urlOverride?.trim();
  if (trimmedOverride) {
    writeStoredHandle(trimmedOverride);
    return trimmedOverride;
  }
  const existing = readStoredHandle();
  if (existing) return existing;
  const generated = randomHandle();
  writeStoredHandle(generated);
  return generated;
}

/** Validate a new handle before saving. 3-32 chars, [a-z0-9-] only. */
export function isValidHandle(handle: string): boolean {
  return /^[a-z0-9][a-z0-9-]{2,31}$/i.test(handle);
}
